import { Component, OnInit } from "@angular/core";
import { TabelaComponent } from '../../shared/tabela/tabela.component';
import { RouterModule } from '@angular/router';
import { RelatorioCaqService } from "../../services";
import { DecimalPipe, formatDate, NgIf, NgTemplateOutlet } from "@angular/common";
import { DadosRelatorioCaqDto, defaultDadosRelatorioCaqDto } from "../../dtos/caq-por-etapa-dto";
import { CarregamentoService } from "../../services/carregamento/carregamento.service";
import { LayoutPrincipalService } from "../../layouts/layout-principal/layout-principal.service";
import { LayoutResultadoService } from "../../layouts/layout-resultado/layout-resultado.service";
import { roundToDecimal } from "../../utils/math-utils";
import { CabecalhoCsv, CsvUtils, DadosCsv } from "../../utils/csv-utils";
import { AnosReferenciasService } from '../../services/anos-referencias/anos-referencias.service';
import { AnosReferenciasDto } from '../../dtos/anos-referencias.dto';
import { MenuSuspensoComponent, OpcaoMenuSuspenso } from "../../shared/menu-suspenso/menu-suspenso.component";
import { MatTooltip } from "@angular/material/tooltip";
import { FeatureFlagService } from "../../services/feature-flag/feature-flag.service";

@Component({
  selector: 'app-relatorio-caq',
  standalone: true,
  imports: [
    TabelaComponent,
    NgIf,
    DecimalPipe,
    RouterModule,
    MenuSuspensoComponent,
    MatTooltip,
    NgTemplateOutlet
  ],
  templateUrl: './relatorio-caq.component.html',
  styleUrl: './relatorio-caq.component.scss',
})
export class RelatorioCaqComponent implements OnInit {
  public dadosRelatorioCaqDto = defaultDadosRelatorioCaqDto;
  public numeroTotalDeMatriculas = 0;
  public anosReferencias: AnosReferenciasDto | null = null;

  public opcoesFiltroLocalizacao: OpcaoMenuSuspenso[] = [
    {
      rotulo: 'Sem filtros',
      icone: 'deselect',
      acao: 'NENHUM',
    },
    {
      rotulo: 'Urbano',
      icone: 'location_city',
      acao: 'URBANO'
    },
     {
      rotulo: 'Campo',
      icone: 'nature',
      acao: 'CAMPO'
    },
    {
      rotulo: 'Terra indígena',
      icone: 'nature',
      acao: 'TERRA_INDIGENA'
    },
    {
      rotulo: 'Comunidade quilombola',
      icone: 'nature',
      acao: 'QUILOMBOLA'
    }
  ]

  public filtroLocalizacaoEscolhido: OpcaoMenuSuspenso = this.opcoesFiltroLocalizacao[0]

  public textoComoCitar: string = ''

  cabecalhosCustoAlunoQualidade = [
    { titulo: 'Etapa', tamanho: '30%' },
    { titulo: 'Área da localidade', tamanho: '11%' },
    { titulo: 'Turno', tamanho: '10%' },
    {
      titulo: 'CAQ por mês (R$)',
      tamanho: '20%',
      subtitulos: ['2021', 'Intervalo de valores <sub>(1)</sub>', '10%menor'],
    },
    { titulo: 'Fator de ponderação <sup>(2)</sup>', tamanho: '13%' },
    { titulo: 'Número de matrículas - 2020 <sup>(3)</sup>', tamanho: '16%' },
  ];

  constructor(
    public relatorioCaqService: RelatorioCaqService,
    private carregamentoService: CarregamentoService,
    private layoutPrincipalService: LayoutPrincipalService,
    private layoutResultadoService: LayoutResultadoService,
    private anosReferenciasService: AnosReferenciasService,
    public featureFlagService: FeatureFlagService
  ) {
    this.relatorioCaqService = relatorioCaqService;
    if (this.featureFlagService.funcionalidadeHabilitada('938-migracao-datas-relatorios-1-e-2')) {
      this.anosReferencias = this.anosReferenciasService.obterAnosReferenciasSnapshot();
    }
  }

  ngOnInit(): void {
    this.layoutResultadoService.setTitulo('Custo Aluno Qualidade');
    this.layoutResultadoService.setCorTexto('text-red-500');
    this.layoutResultadoService.setCorBordaTitulo('border-red-500');
    this.layoutResultadoService.setEditavel(false);

    this.layoutResultadoService.setCorTextoRodape('text-red-500')
    this.layoutResultadoService.setCorBotaoRodape('bg-red-500');
    this.layoutResultadoService.setCorBordaBotaoRodape('border-red-500')

    this.layoutResultadoService.setRedeSelecionada('Plano nacional');
    this.layoutResultadoService.setPais('Brasil');
    // #1206: o CAQ é sempre nacional — limpa estado/cidade que ficam retidos no
    // LayoutResultadoService quando o usuário vem de um relatório com localidade.
    this.layoutResultadoService.setEstado('');
    this.layoutResultadoService.setCidade('');
    this.layoutResultadoService.setAno(String(this.anosReferencias?.anoPqr ?? 2026));

    // #1314: o mapa da sidebar é definido pelo LayoutResultadoComponent a partir
    // da localidade selecionada em outro relatório; como o CAQ é sempre nacional,
    // força o mapa do Brasil para não exibir o da consulta anterior.
    this.layoutResultadoService.setImagemFundo('assets/ilustrations/Maps/Estado=BR, Size=MD, Stroke=True.svg');
    // O CAQ não tem seleção de localidade: limpa a rota retida do relatório
    // anterior para o botão "Alterar localidade" não aparecer.
    this.layoutResultadoService.setRotaFiltroLocalidade('');

    this.layoutResultadoService.setRotaApresentacao('/caq-apresentacao');

    this.layoutResultadoService.setDownloads([
      {
        tipo: 'acao',
        texto: 'Baixe os valores do CAQ em CSV',
        acao: () => this.downloadCaqCsv(this.dadosRelatorioCaqDto)
      },
      {
        tipo: 'rota',
        texto: 'Baixe os valores por escola (base de cálculo dos valores do CAQ nacional) em CSV',
        rota: 'simulador/caq/nacional/detalhado/csv'
      }
    ]);

    this.layoutResultadoService.setTextoComoCitar(`SIMULADOR de Custo-Aluno Qualidade: Valores do Custo-Aluno Qualidade
      (CAQ) para o Brasil, ${this.anosReferencias?.anoPqr ?? 2026}. Disponível em: <https://simcaq.ufg.br/>. Acesso em: ${formatDate(new Date(),
      'dd MMM yyyy', 'pt-BR')}.`);

    this.layoutPrincipalService.definaMensagensCarregamento(['Obtendo dados...']);
    this.carregamentoService.startLoading();

    this.relatorioCaqService
      .solicitarRelatorioCaqNacional()
      .subscribe((dadosRelatorioCaqDto) => {
        this.dadosRelatorioCaqDto = dadosRelatorioCaqDto;

          this.totalizarMatriculas();

          this.carregamentoService.finishLoading();
      });


  }

  private totalizarMatriculas(): void{
    const n = (v?: number | null):number => v ?? 0;

        const filtro = this.filtroLocalizacao;
        const incluirUrbanoSemFiltro = filtro === 'NENHUM';
        const incluirUrbano = filtro === 'URBANO';
        const incluirRural = filtro === 'NENHUM';
        const incluirCampo = filtro === 'CAMPO';
        const incluirTerraIndigena = filtro === 'TERRA_INDIGENA';
        const incluirQuilombola = filtro === 'QUILOMBOLA';

        this.numeroTotalDeMatriculas =
          // Creche
          (incluirUrbanoSemFiltro ? n(this.dadosRelatorioCaqDto.crecheUrbanaIndepLocDiferenciadaParcial?.numeroMatriculas) + n(this.dadosRelatorioCaqDto.crecheUrbanaIndepLocDiferenciadaIntegral?.numeroMatriculas) : 0) +
          (incluirUrbano ? n(this.dadosRelatorioCaqDto.crecheUrbanaParcial?.numeroMatriculas) + n(this.dadosRelatorioCaqDto.crecheUrbanaIntegral?.numeroMatriculas) : 0) +
          (incluirRural ? n(this.dadosRelatorioCaqDto.crecheRuralParcial?.numeroMatriculas) + n(this.dadosRelatorioCaqDto.crecheRuralIntegral?.numeroMatriculas) : 0) +
          (incluirCampo ? n(this.dadosRelatorioCaqDto.crecheCampoParcial?.numeroMatriculas) + n(this.dadosRelatorioCaqDto.crecheCampoIntegral?.numeroMatriculas) : 0) +
          (incluirTerraIndigena ? n(this.dadosRelatorioCaqDto.crecheTerraIndigenaParcial?.numeroMatriculas) + n(this.dadosRelatorioCaqDto.crecheTerraIndigenaIntegral?.numeroMatriculas) : 0) +
          (incluirQuilombola ? n(this.dadosRelatorioCaqDto.crecheQuilombolaParcial?.numeroMatriculas) + n(this.dadosRelatorioCaqDto.crecheQuilombolaIntegral?.numeroMatriculas) : 0) +
          // Pré-escola
          (incluirUrbanoSemFiltro ? n(this.dadosRelatorioCaqDto.preEscolaUrbanaIndepLocDiferenciadaParcial?.numeroMatriculas) + n(this.dadosRelatorioCaqDto.preEscolaUrbanaIndepLocDiferenciadaIntegral?.numeroMatriculas) : 0) +
          (incluirUrbano ? n(this.dadosRelatorioCaqDto.preEscolaUrbanaParcial?.numeroMatriculas) + n(this.dadosRelatorioCaqDto.preEscolaUrbanaIntegral?.numeroMatriculas) : 0) +
          (incluirRural ? n(this.dadosRelatorioCaqDto.preEscolaRuralParcial?.numeroMatriculas) + n(this.dadosRelatorioCaqDto.preEscolaRuralIntegral?.numeroMatriculas) : 0) +
          (incluirCampo ? n(this.dadosRelatorioCaqDto.preEscolaCampoParcial?.numeroMatriculas) + n(this.dadosRelatorioCaqDto.preEscolaCampoIntegral?.numeroMatriculas) : 0) +
          (incluirTerraIndigena ? n(this.dadosRelatorioCaqDto.preEscolaTerraIndigenaParcial?.numeroMatriculas) + n(this.dadosRelatorioCaqDto.preEscolaTerraIndigenaIntegral?.numeroMatriculas) : 0) +
          (incluirQuilombola ? n(this.dadosRelatorioCaqDto.preEscolaQuilombolaParcial?.numeroMatriculas) + n(this.dadosRelatorioCaqDto.preEscolaQuilombolaIntegral?.numeroMatriculas) : 0) +
          // Ensino Fundamental - Anos Iniciais
          (incluirUrbanoSemFiltro ? n(this.dadosRelatorioCaqDto.ensFunAnosIniciaisUrbanaIndepLocDiferenciadaParcial?.numeroMatriculas) + n(this.dadosRelatorioCaqDto.ensFunAnosIniciaisUrbanaIndepLocDiferenciadaIntegral?.numeroMatriculas) : 0) +
          (incluirUrbano ? n(this.dadosRelatorioCaqDto.ensFunAnosIniciaisUrbanaParcial?.numeroMatriculas) + n(this.dadosRelatorioCaqDto.ensFunAnosIniciaisUrbanaIntegral?.numeroMatriculas) : 0) +
          (incluirRural ? n(this.dadosRelatorioCaqDto.ensFunAnosIniciaisRuralParcial?.numeroMatriculas) + n(this.dadosRelatorioCaqDto.ensFunAnosIniciaisRuralIntegral?.numeroMatriculas) : 0) +
          (incluirCampo ? n(this.dadosRelatorioCaqDto.ensFunAnosIniciaisCampoParcial?.numeroMatriculas) + n(this.dadosRelatorioCaqDto.ensFunAnosIniciaisCampoIntegral?.numeroMatriculas) : 0) +
          (incluirTerraIndigena ? n(this.dadosRelatorioCaqDto.ensFunAnosIniciaisTerraIndigenaParcial?.numeroMatriculas) + n(this.dadosRelatorioCaqDto.ensFunAnosIniciaisTerraIndigenaIntegral?.numeroMatriculas) : 0) +
          (incluirQuilombola ? n(this.dadosRelatorioCaqDto.ensFunAnosIniciaisQuilombolaParcial?.numeroMatriculas) + n(this.dadosRelatorioCaqDto.ensFunAnosIniciaisQuilombolaIntegral?.numeroMatriculas) : 0) +
          // Ensino Fundamental - Anos Finais
          (incluirUrbanoSemFiltro ? n(this.dadosRelatorioCaqDto.ensFunAnosFinaisUrbanaIndepLocDiferenciadaParcial?.numeroMatriculas) + n(this.dadosRelatorioCaqDto.ensFunAnosFinaisUrbanaIndepLocDiferenciadaIntegral?.numeroMatriculas) : 0) +
          (incluirUrbano ? n(this.dadosRelatorioCaqDto.ensFunAnosFinaisUrbanaParcial?.numeroMatriculas) + n(this.dadosRelatorioCaqDto.ensFunAnosFinaisUrbanaIntegral?.numeroMatriculas) : 0) +
          (incluirRural ? n(this.dadosRelatorioCaqDto.ensFunAnosFinaisRuralParcial?.numeroMatriculas) + n(this.dadosRelatorioCaqDto.ensFunAnosFinaisRuralIntegral?.numeroMatriculas) : 0) +
          (incluirCampo ? n(this.dadosRelatorioCaqDto.ensFunAnosFinaisCampoParcial?.numeroMatriculas) + n(this.dadosRelatorioCaqDto.ensFunAnosFinaisCampoIntegral?.numeroMatriculas) : 0) +
          (incluirTerraIndigena ? n(this.dadosRelatorioCaqDto.ensFunAnosFinaisTerraIndigenaParcial?.numeroMatriculas) + n(this.dadosRelatorioCaqDto.ensFunAnosFinaisTerraIndigenaIntegral?.numeroMatriculas) : 0) +
          (incluirQuilombola ? n(this.dadosRelatorioCaqDto.ensFunAnosFinaisQuilombolaParcial?.numeroMatriculas) + n(this.dadosRelatorioCaqDto.ensFunAnosFinaisQuilombolaIntegral?.numeroMatriculas) : 0) +
          // Ensino Médio
          (incluirUrbanoSemFiltro ? n(this.dadosRelatorioCaqDto.ensMedioUrbanaIndepLocDiferenciadaParcial?.numeroMatriculas) + n(this.dadosRelatorioCaqDto.ensMedioUrbanaIndepLocDiferenciadaIntegral?.numeroMatriculas) : 0) +
          (incluirUrbano ? n(this.dadosRelatorioCaqDto.ensMedioUrbanaParcial?.numeroMatriculas) + n(this.dadosRelatorioCaqDto.ensMedioUrbanaIntegral?.numeroMatriculas) : 0) +
          (incluirRural ? n(this.dadosRelatorioCaqDto.ensMedioRuralParcial?.numeroMatriculas) + n(this.dadosRelatorioCaqDto.ensMedioRuralIntegral?.numeroMatriculas) : 0) +
          (incluirCampo ? n(this.dadosRelatorioCaqDto.ensMedioCampoParcial?.numeroMatriculas) + n(this.dadosRelatorioCaqDto.ensMedioCampoIntegral?.numeroMatriculas) : 0) +
          (incluirTerraIndigena ? n(this.dadosRelatorioCaqDto.ensMedioTerraIndigenaParcial?.numeroMatriculas) + n(this.dadosRelatorioCaqDto.ensMedioTerraIndigenaIntegral?.numeroMatriculas) : 0) +
          (incluirQuilombola ? n(this.dadosRelatorioCaqDto.ensMedioQuilombolaParcial?.numeroMatriculas) + n(this.dadosRelatorioCaqDto.ensMedioQuilombolaIntegral?.numeroMatriculas) : 0) +
          // EJA (só parcial)
          (incluirUrbanoSemFiltro ? n(this.dadosRelatorioCaqDto.ejaUrbanaIndepLocDiferenciadaParcial?.numeroMatriculas) : 0) +
          (incluirUrbano ? n(this.dadosRelatorioCaqDto.ejaUrbanaParcial?.numeroMatriculas) : 0) +
          (incluirRural ? n(this.dadosRelatorioCaqDto.ejaRuralParcial?.numeroMatriculas) : 0) +
          (incluirCampo ? n(this.dadosRelatorioCaqDto.ejaCampoParcial?.numeroMatriculas) : 0) +
          (incluirTerraIndigena ? n(this.dadosRelatorioCaqDto.ejaTerraIndigenaParcial?.numeroMatriculas) : 0) +
          (incluirQuilombola ? n(this.dadosRelatorioCaqDto.ejaQuilombolaParcial?.numeroMatriculas) : 0);
  }

  /**
   * Calcula a largura da barra visual para o CAQ na tabela principal
   * usa o maior valor como referência
   */
  calcularLarguraBarraCAQTabela(valorMediana: number): number {
    const larguraMaximaEmPx = 170;

    const semFiltro = this.filtroLocalizacao === "NENHUM";
    const filtroCampo = this.filtroLocalizacao === "CAMPO";
    const filtroTerraIndigena = this.filtroLocalizacao === "TERRA_INDIGENA";
    const filtroQuilombola = this.filtroLocalizacao === "QUILOMBOLA";

    if (!this.dadosRelatorioCaqDto) return 0;

    let valores: number[] = [];

    if (filtroCampo) {
        valores = [
          this.dadosRelatorioCaqDto.crecheCampoParcial?.mediana || 0,
          this.dadosRelatorioCaqDto.crecheCampoIntegral?.mediana || 0,
          this.dadosRelatorioCaqDto.preEscolaCampoParcial?.mediana || 0,
          this.dadosRelatorioCaqDto.preEscolaCampoIntegral?.mediana || 0,
          this.dadosRelatorioCaqDto.ensFunAnosIniciaisCampoParcial?.mediana || 0,
          this.dadosRelatorioCaqDto.ensFunAnosIniciaisCampoIntegral?.mediana || 0,
          this.dadosRelatorioCaqDto.ensFunAnosFinaisCampoParcial?.mediana || 0,
          this.dadosRelatorioCaqDto.ensFunAnosFinaisCampoIntegral?.mediana || 0,
          this.dadosRelatorioCaqDto.ensMedioCampoParcial?.mediana || 0,
          this.dadosRelatorioCaqDto.ensMedioCampoIntegral?.mediana || 0,
          this.dadosRelatorioCaqDto.ejaCampoParcial?.mediana || 0
        ];
    } else if (filtroTerraIndigena) {
        valores = [
          this.dadosRelatorioCaqDto.crecheTerraIndigenaParcial?.mediana || 0,
          this.dadosRelatorioCaqDto.crecheTerraIndigenaIntegral?.mediana || 0,
          this.dadosRelatorioCaqDto.preEscolaTerraIndigenaParcial?.mediana || 0,
          this.dadosRelatorioCaqDto.preEscolaTerraIndigenaIntegral?.mediana || 0,
          this.dadosRelatorioCaqDto.ensFunAnosIniciaisTerraIndigenaParcial?.mediana || 0,
          this.dadosRelatorioCaqDto.ensFunAnosIniciaisTerraIndigenaIntegral?.mediana || 0,
          this.dadosRelatorioCaqDto.ensFunAnosFinaisTerraIndigenaParcial?.mediana || 0,
          this.dadosRelatorioCaqDto.ensFunAnosFinaisTerraIndigenaIntegral?.mediana || 0,
          this.dadosRelatorioCaqDto.ensMedioTerraIndigenaParcial?.mediana || 0,
          this.dadosRelatorioCaqDto.ensMedioTerraIndigenaIntegral?.mediana || 0,
          this.dadosRelatorioCaqDto.ejaTerraIndigenaParcial?.mediana || 0
        ];
    } else if (filtroQuilombola) {
        valores = [
          this.dadosRelatorioCaqDto.crecheQuilombolaParcial?.mediana || 0,
          this.dadosRelatorioCaqDto.crecheQuilombolaIntegral?.mediana || 0,
          this.dadosRelatorioCaqDto.preEscolaQuilombolaParcial?.mediana || 0,
          this.dadosRelatorioCaqDto.preEscolaQuilombolaIntegral?.mediana || 0,
          this.dadosRelatorioCaqDto.ensFunAnosIniciaisQuilombolaParcial?.mediana || 0,
          this.dadosRelatorioCaqDto.ensFunAnosIniciaisQuilombolaIntegral?.mediana || 0,
          this.dadosRelatorioCaqDto.ensFunAnosFinaisQuilombolaParcial?.mediana || 0,
          this.dadosRelatorioCaqDto.ensFunAnosFinaisQuilombolaIntegral?.mediana || 0,
          this.dadosRelatorioCaqDto.ensMedioQuilombolaParcial?.mediana || 0,
          this.dadosRelatorioCaqDto.ensMedioQuilombolaIntegral?.mediana || 0,
          this.dadosRelatorioCaqDto.ejaQuilombolaParcial?.mediana || 0
        ];
    } else {
        valores = [
          (semFiltro ? this.dadosRelatorioCaqDto.crecheUrbanaIndepLocDiferenciadaParcial?.mediana : this.dadosRelatorioCaqDto.crecheUrbanaParcial?.mediana) || 0,
          (semFiltro ? this.dadosRelatorioCaqDto.crecheUrbanaIndepLocDiferenciadaIntegral?.mediana : this.dadosRelatorioCaqDto.crecheUrbanaIntegral?.mediana) || 0,
          this.dadosRelatorioCaqDto.crecheRuralParcial?.mediana || 0,
          this.dadosRelatorioCaqDto.crecheRuralIntegral?.mediana || 0,
          (semFiltro ? this.dadosRelatorioCaqDto.preEscolaUrbanaIndepLocDiferenciadaParcial?.mediana : this.dadosRelatorioCaqDto.preEscolaUrbanaParcial?.mediana) || 0,
          (semFiltro ? this.dadosRelatorioCaqDto.preEscolaUrbanaIndepLocDiferenciadaIntegral?.mediana : this.dadosRelatorioCaqDto.preEscolaUrbanaIntegral?.mediana) || 0,
          this.dadosRelatorioCaqDto.preEscolaRuralParcial?.mediana || 0,
          this.dadosRelatorioCaqDto.preEscolaRuralIntegral?.mediana || 0,
          (semFiltro ? this.dadosRelatorioCaqDto.ensFunAnosIniciaisUrbanaIndepLocDiferenciadaParcial?.mediana : this.dadosRelatorioCaqDto.ensFunAnosIniciaisUrbanaParcial?.mediana) || 0,
          (semFiltro ? this.dadosRelatorioCaqDto.ensFunAnosIniciaisUrbanaIndepLocDiferenciadaIntegral?.mediana : this.dadosRelatorioCaqDto.ensFunAnosIniciaisUrbanaIntegral?.mediana) || 0,
          this.dadosRelatorioCaqDto.ensFunAnosIniciaisRuralParcial?.mediana || 0,
          this.dadosRelatorioCaqDto.ensFunAnosIniciaisRuralIntegral?.mediana || 0,
          (semFiltro ? this.dadosRelatorioCaqDto.ensFunAnosFinaisUrbanaIndepLocDiferenciadaParcial?.mediana : this.dadosRelatorioCaqDto.ensFunAnosFinaisUrbanaParcial?.mediana) || 0,
          (semFiltro ? this.dadosRelatorioCaqDto.ensFunAnosFinaisUrbanaIndepLocDiferenciadaIntegral?.mediana : this.dadosRelatorioCaqDto.ensFunAnosFinaisUrbanaIntegral?.mediana) || 0,
          this.dadosRelatorioCaqDto.ensFunAnosFinaisRuralParcial?.mediana || 0,
          this.dadosRelatorioCaqDto.ensFunAnosFinaisRuralIntegral?.mediana || 0,
          (semFiltro ? this.dadosRelatorioCaqDto.ensMedioUrbanaIndepLocDiferenciadaParcial?.mediana : this.dadosRelatorioCaqDto.ensMedioUrbanaParcial?.mediana) || 0,
          (semFiltro ? this.dadosRelatorioCaqDto.ensMedioUrbanaIndepLocDiferenciadaIntegral?.mediana : this.dadosRelatorioCaqDto.ensMedioUrbanaIntegral?.mediana) || 0,
          this.dadosRelatorioCaqDto.ensMedioRuralParcial?.mediana || 0,
          this.dadosRelatorioCaqDto.ensMedioRuralIntegral?.mediana || 0,
          (semFiltro ? this.dadosRelatorioCaqDto.ejaUrbanaIndepLocDiferenciadaParcial?.mediana : this.dadosRelatorioCaqDto.ejaUrbanaParcial?.mediana) || 0,
          this.dadosRelatorioCaqDto.ejaRuralParcial?.mediana || 0
        ];
    }

    const valorMaximo = Math.max(...valores);

    if (valorMaximo === 0 || valorMediana === 0) return 0;

    return Math.round((valorMediana / valorMaximo) * larguraMaximaEmPx);
  }

  downloadParametrosCaqCsv(): void{
    this.downloadCaqCsv(this.dadosRelatorioCaqDto);
  }

  downloadCaqCsv(caq: DadosRelatorioCaqDto): void {
    const cabecalho: CabecalhoCsv[] = [
      { chave: 'etapa', rotulo: 'Etapa' },
      { chave: 'localidade', rotulo: 'Área da localidade' },
      { chave: 'turno', rotulo: 'Turno' },
      { chave: 'mediana', rotulo: `CAQ Mediana ${this.anosReferencias?.anoPqr ?? 2026} (R$)` },
      { chave: 'fator', rotulo: 'Fator de ponderação' },
      { chave: 'matricula', rotulo: `Número de matrículas - ${this.anosReferencias?.anoCenso ?? 2025}` },
      { chave: 'escola', rotulo: `Número de escolas - ${this.anosReferencias?.anoCenso ?? 2025}` },
    ];

    const dados: any[] = [];

    const adicionarLinha = (
      etapa: string,
      localidade: string,
      turno: string,
      dadosEtapa?: { mediana: number, fatorPonderacao: number, numeroMatriculas: number, numeroEscolas: number }
    ) => {
      if(dadosEtapa && dadosEtapa.mediana){
          dados.push({
            etapa,
            localidade,
            turno,
            mediana: roundToDecimal(dadosEtapa.mediana, 0),
            fator: roundToDecimal(dadosEtapa.fatorPonderacao, 2),
            matricula: roundToDecimal(dadosEtapa.numeroMatriculas, 0),
            escola: roundToDecimal(dadosEtapa.numeroEscolas, 0),
          });
      }
    };

    const semFiltro = this.filtroLocalizacao === "NENHUM";

    if(this.filtroLocalizacao === "NENHUM" || this.filtroLocalizacao === "URBANO"){
        adicionarLinha('Creche', 'Urbana', 'Parcial', semFiltro ? caq.crecheUrbanaIndepLocDiferenciadaParcial : caq.crecheUrbanaParcial);
        adicionarLinha('Creche', 'Urbana', 'Integral', semFiltro ? caq.crecheUrbanaIndepLocDiferenciadaIntegral : caq.crecheUrbanaIntegral);
    }

    if(this.filtroLocalizacao === "NENHUM"){
        adicionarLinha('Creche', 'Rural', 'Parcial', caq.crecheRuralParcial);
        adicionarLinha('Creche', 'Rural', 'Integral', caq.crecheRuralIntegral);
    }

    if(this.filtroLocalizacao === "CAMPO"){
        adicionarLinha('Creche', 'Campo', 'Parcial', caq.crecheCampoParcial);
        adicionarLinha('Creche', 'Campo', 'Integral', caq.crecheCampoIntegral);
    }

    if(this.filtroLocalizacao === "TERRA_INDIGENA"){
        adicionarLinha('Creche', 'Terra Indígena', 'Parcial', caq.crecheTerraIndigenaParcial);
        adicionarLinha('Creche', 'Terra Indígena', 'Integral', caq.crecheTerraIndigenaIntegral);
    }

    if(this.filtroLocalizacao === "QUILOMBOLA"){
        adicionarLinha('Creche', 'Quilombola', 'Parcial', caq.crecheQuilombolaParcial);
        adicionarLinha('Creche', 'Quilombola', 'Integral', caq.crecheQuilombolaIntegral);
    }


    if(this.filtroLocalizacao === "NENHUM" || this.filtroLocalizacao === "URBANO"){
        adicionarLinha('Pré-Escola', 'Urbana', 'Parcial', semFiltro ? caq.preEscolaUrbanaIndepLocDiferenciadaParcial : caq.preEscolaUrbanaParcial);
        adicionarLinha('Pré-Escola', 'Urbana', 'Integral', semFiltro ? caq.preEscolaUrbanaIndepLocDiferenciadaIntegral : caq.preEscolaUrbanaIntegral);
    }

    if(this.filtroLocalizacao === "NENHUM"){
        adicionarLinha('Pré-Escola', 'Rural', 'Parcial', caq.preEscolaRuralParcial);
        adicionarLinha('Pré-Escola', 'Rural', 'Integral', caq.preEscolaRuralIntegral);
    }

    if(this.filtroLocalizacao === "CAMPO"){
        adicionarLinha('Pré-Escola', 'Campo', 'Parcial', caq.preEscolaCampoParcial);
        adicionarLinha('Pré-Escola', 'Campo', 'Integral', caq.preEscolaCampoIntegral);
    }

    if(this.filtroLocalizacao === "TERRA_INDIGENA"){
        adicionarLinha('Pré-Escola', 'Terra Indígena', 'Parcial', caq.preEscolaTerraIndigenaParcial);
        adicionarLinha('Pré-Escola', 'Terra Indígena', 'Integral', caq.preEscolaTerraIndigenaIntegral);
    }

    if(this.filtroLocalizacao === "QUILOMBOLA"){
        adicionarLinha('Pré-Escola', 'Quilombola', 'Parcial', caq.preEscolaQuilombolaParcial);
        adicionarLinha('Pré-Escola', 'Quilombola', 'Integral', caq.preEscolaQuilombolaIntegral);
    }


    if(this.filtroLocalizacao === "NENHUM" || this.filtroLocalizacao === "URBANO"){
        adicionarLinha('Ensino Fundamental - anos iniciais', 'Urbana', 'Parcial', semFiltro ? caq.ensFunAnosIniciaisUrbanaIndepLocDiferenciadaParcial : caq.ensFunAnosIniciaisUrbanaParcial);
        adicionarLinha('Ensino Fundamental - anos iniciais', 'Urbana', 'Integral', semFiltro ? caq.ensFunAnosIniciaisUrbanaIndepLocDiferenciadaIntegral : caq.ensFunAnosIniciaisUrbanaIntegral);
    }

    if(this.filtroLocalizacao === "NENHUM"){
        adicionarLinha('Ensino Fundamental - anos iniciais', 'Rural', 'Parcial', caq.ensFunAnosIniciaisRuralParcial);
        adicionarLinha('Ensino Fundamental - anos iniciais', 'Rural', 'Integral', caq.ensFunAnosIniciaisRuralIntegral);
    }

    if(this.filtroLocalizacao === "CAMPO"){
        adicionarLinha('Ensino Fundamental - anos iniciais', 'Campo', 'Parcial', caq.ensFunAnosIniciaisCampoParcial);
        adicionarLinha('Ensino Fundamental - anos iniciais', 'Campo', 'Integral', caq.ensFunAnosIniciaisCampoIntegral);
    }

    if(this.filtroLocalizacao === "TERRA_INDIGENA"){
        adicionarLinha('Ensino Fundamental - anos iniciais', 'Terra Indígena', 'Parcial', caq.ensFunAnosIniciaisTerraIndigenaParcial);
        adicionarLinha('Ensino Fundamental - anos iniciais', 'Terra Indígena', 'Integral', caq.ensFunAnosIniciaisTerraIndigenaIntegral);
    }

    if(this.filtroLocalizacao === "QUILOMBOLA"){
        adicionarLinha('Ensino Fundamental - anos iniciais', 'Quilombola', 'Parcial', caq.ensFunAnosIniciaisQuilombolaParcial);
        adicionarLinha('Ensino Fundamental - anos iniciais', 'Quilombola', 'Integral', caq.ensFunAnosIniciaisQuilombolaIntegral);
    }

    if(this.filtroLocalizacao === "NENHUM" || this.filtroLocalizacao === "URBANO"){
        adicionarLinha('Ensino Fundamental - anos finais', 'Urbana', 'Parcial', semFiltro ? caq.ensFunAnosFinaisUrbanaIndepLocDiferenciadaParcial : caq.ensFunAnosFinaisUrbanaParcial);
        adicionarLinha('Ensino Fundamental - anos finais', 'Urbana', 'Integral', semFiltro ? caq.ensFunAnosFinaisUrbanaIndepLocDiferenciadaIntegral : caq.ensFunAnosFinaisUrbanaIntegral);
    }

    if(this.filtroLocalizacao === "NENHUM"){
        adicionarLinha('Ensino Fundamental - anos finais', 'Rural', 'Parcial', caq.ensFunAnosFinaisRuralParcial);
        adicionarLinha('Ensino Fundamental - anos finais', 'Rural', 'Integral', caq.ensFunAnosFinaisRuralIntegral);
    }

    if(this.filtroLocalizacao === "CAMPO"){
        adicionarLinha('Ensino Fundamental - anos finais', 'Campo', 'Parcial', caq.ensFunAnosFinaisCampoParcial);
        adicionarLinha('Ensino Fundamental - anos finais', 'Campo', 'Integral', caq.ensFunAnosFinaisCampoIntegral);
    }

    if(this.filtroLocalizacao === "TERRA_INDIGENA"){
        adicionarLinha('Ensino Fundamental - anos finais', 'Terra Indígena', 'Parcial', caq.ensFunAnosFinaisTerraIndigenaParcial);
        adicionarLinha('Ensino Fundamental - anos finais', 'Terra Indígena', 'Integral', caq.ensFunAnosFinaisTerraIndigenaIntegral);
    }

    if(this.filtroLocalizacao === "QUILOMBOLA"){
        adicionarLinha('Ensino Fundamental - anos finais', 'Quilombola', 'Parcial', caq.ensFunAnosFinaisQuilombolaParcial);
        adicionarLinha('Ensino Fundamental - anos finais', 'Quilombola', 'Integral', caq.ensFunAnosFinaisQuilombolaIntegral);
    }

    if(this.filtroLocalizacao === "NENHUM" || this.filtroLocalizacao === "URBANO"){
        adicionarLinha('Ensino Médio', 'Urbana', 'Parcial', semFiltro ? caq.ensMedioUrbanaIndepLocDiferenciadaParcial : caq.ensMedioUrbanaParcial);
        adicionarLinha('Ensino Médio', 'Urbana', 'Integral', semFiltro ? caq.ensMedioUrbanaIndepLocDiferenciadaIntegral : caq.ensMedioUrbanaIntegral);
    }

    if(this.filtroLocalizacao === "NENHUM"){
        adicionarLinha('Ensino Médio', 'Rural', 'Parcial', caq.ensMedioRuralParcial);
        adicionarLinha('Ensino Médio', 'Rural', 'Integral', caq.ensMedioRuralIntegral);
    }

    if(this.filtroLocalizacao === "CAMPO"){
        adicionarLinha('Ensino Médio', 'Campo', 'Parcial', caq.ensMedioCampoParcial);
        adicionarLinha('Ensino Médio', 'Campo', 'Integral', caq.ensMedioCampoIntegral);
    }

    if(this.filtroLocalizacao === "TERRA_INDIGENA"){
        adicionarLinha('Ensino Médio', 'Terra Indígena', 'Parcial', caq.ensMedioTerraIndigenaParcial);
        adicionarLinha('Ensino Médio', 'Terra Indígena', 'Integral', caq.ensMedioTerraIndigenaIntegral);
    }

    if(this.filtroLocalizacao === "QUILOMBOLA"){
        adicionarLinha('Ensino Médio', 'Quilombola', 'Parcial', caq.ensMedioQuilombolaParcial);
        adicionarLinha('Ensino Médio', 'Quilombola', 'Integral', caq.ensMedioQuilombolaIntegral);
    }

    if(this.filtroLocalizacao === "NENHUM" || this.filtroLocalizacao === "URBANO"){
        adicionarLinha('EJA', 'Urbana', 'Parcial', semFiltro ? caq.ejaUrbanaIndepLocDiferenciadaParcial : caq.ejaUrbanaParcial);
    }

    if(this.filtroLocalizacao === "NENHUM"){
        adicionarLinha('EJA', 'Rural', 'Parcial', caq.ejaRuralParcial);
    }

    if(this.filtroLocalizacao === "CAMPO"){
        adicionarLinha('EJA', 'Campo', 'Parcial', caq.ejaCampoParcial);
    }

    if(this.filtroLocalizacao === "TERRA_INDIGENA"){
        adicionarLinha('EJA', 'Terra Indígena', 'Parcial', caq.ejaTerraIndigenaParcial);
    }

    if(this.filtroLocalizacao === "QUILOMBOLA"){
        adicionarLinha('EJA', 'Quilombola', 'Parcial', caq.ejaQuilombolaParcial);
    }

    dados.push({
      etapa: 'TOTAL',
      localidade: '',
      turno: '',
      mediana: '',
      fator: '',
      matricula: roundToDecimal(this.numeroTotalDeMatriculas, 0),
      escola: ''
    });

    const dadosCsv: DadosCsv = {
      cabecalho,
      dados,
      nome: 'Custo-aluno qualidade'
    };

    CsvUtils.baixar(dadosCsv, String(this.anosReferencias?.anoPqr ?? 2026), 'Brasil');
  }
  chamarAcao(acao: string): void {

    const escolhido = this.opcoesFiltroLocalizacao.filter(f=>f.acao===acao)

    if(escolhido.length>0){
      this.filtroLocalizacaoEscolhido = escolhido[0]
      this.totalizarMatriculas()
      return
    }

    this.filtroLocalizacaoEscolhido = this.opcoesFiltroLocalizacao[0]
    this.totalizarMatriculas()

  }

  public get filtroLocalizacao(): string{
    return this.filtroLocalizacaoEscolhido.acao;
  }

}
